import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProjectGateway } from '../../gateways/project.gateway';
import { AuthService } from '../../services/auth.service';
import type { Project } from '../../models/project.model';

type ListState = 'loading' | 'ready' | 'error';

@Component({
    selector: 'app-projects-list',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './projects-list.html',
    styleUrl: './projects-list.css',
})
export class ProjectsList implements OnInit {
    private readonly router = inject(Router);
    private readonly gateway = inject(ProjectGateway);
    private readonly auth = inject(AuthService);
    private readonly fb = inject(FormBuilder);

    readonly state = signal<ListState>('loading');
    readonly projects = signal<Project[]>([]);
    readonly loadError = signal('');

    readonly showCreate = signal(false);
    readonly creating = signal(false);
    readonly createError = signal('');

    readonly form = this.fb.nonNullable.group({
        name: ['', Validators.required],
        description: ['', Validators.maxLength(256)],
    });

    ngOnInit(): void {
        this.loadProjects();
    }

    private loadProjects(): void {
        this.state.set('loading');
        this.loadError.set('');
        this.gateway.listProjects(this.auth.user()?.sub).subscribe({
            next: (projects) => {
                this.projects.set(projects);
                this.state.set('ready');
            },
            error: (err: Error) => {
                this.loadError.set(err.message);
                this.state.set('error');
            },
        });
    }

    openProject(projectId: string): void {
        this.router.navigate(['/projects', projectId]);
    }

    toggleCreate(): void {
        this.createError.set('');
        this.showCreate.update((v) => !v);
    }

    isInvalid(control: 'name' | 'description'): boolean {
        const c = this.form.controls[control];
        return c.invalid && (c.dirty || c.touched);
    }

    createProject(): void {
        if (this.form.invalid || this.creating()) {
            this.form.markAllAsTouched();
            return;
        }
        this.createError.set('');

        const creatorId = this.auth.user()?.sub;
        if (!creatorId) {
            this.createError.set(
                'You must be signed in to create a project. Please sign in and try again.',
            );
            return;
        }

        const value = this.form.getRawValue();
        this.creating.set(true);
        this.gateway
            .createProject({ name: value.name, description: value.description, creatorId })
            .subscribe({
                next: (project) => {
                    this.router.navigate(['/projects', project.id]);
                },
                error: (err: Error) => {
                    this.creating.set(false);
                    this.createError.set(err.message);
                },
            });
    }

    deleteProject(projectId: string, event: Event): void {
        event.stopPropagation();
        this.gateway.deleteProject(projectId).subscribe({
            next: () => {
                this.projects.update((list) => list.filter((p) => p.id !== projectId));
            },
            error: (err: Error) => {
                this.loadError.set(err.message);
            },
        });
    }
}
